import { useKlaviyo } from "@hooks/useKlaviyo";
import { useTemplate } from "@hooks/useTemplate";
import { ComponentProps } from "@ts/components";
import React, {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useState,
} from "react";
import { z } from "zod";
import { shoeFitLength, shoeFitWidth } from "./withFitFeedback";
import { useFunctionsGatsby } from "@hooks/useFunctionsGatsby";

export type ErrorMap = {
  [key: string]: string;
};

type ReviewsFormInputProps = ComponentProps & {
  sku: string;
  gender?: "Mens" | "Womens";
  classname?: string;
};

type ReviewsFormOutputProps = Omit<ReviewsFormInputProps, "sku"> & {
  additionalReviewsFormSubmitingText?: string;
  additionalReviewsFormSubmitText?: string;
  additionalReviewsFormErrorMessage?: string;
  additionalReviewsFormSuccessTitle?: string;
  additionalReviewsFormSuccessMessage?: string;
  data: {
    name: string;
    email: string;
    title: string;
    content: string;
  };
  errors: ErrorMap;
  fitLength: string;
  fitWidth: string;
  handleChange: ({ target: { name, value } }) => void;
  handleFitLengthChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleFitWidthChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleOptionChange: (value: any) => void;
  handleRatingChange: (quantity: number) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  loading: boolean;
  rating: number;
  success: boolean;
};

const schema = z.object({
  email: z
    .string()
    .email("Email is invalid")
    .nonempty("This field is required"),
  content: z.string().nonempty("This field is required"),
  name: z.string().nonempty("This field is required"),
});
const sizeSchema = z.object({
  size: z.string().nonempty("This field is required"),
});

export const withReviewsForm =
  (Component: FC<ReviewsFormOutputProps>) =>
  ({ name = "ReviewsForm", sku, className, gender }: ReviewsFormInputProps) => {
    const { productReviewAdd, loading } = useFunctionsGatsby();
    const { track } = useKlaviyo();
    const {
      product: {
        additionalReviewsFormSubmitingText,
        additionalReviewsFormSubmitText,
        additionalReviewsFormErrorMessage,
        additionalReviewsFormSuccessTitle,
        additionalReviewsFormSuccessMessage,
      },
    } = useTemplate();

    const [fitLength, setFitLength] = useState(shoeFitLength?.[1]?.value);
    const [fitWidth, setFitWidth] = useState(shoeFitWidth?.[1]?.value);
    const [data, setData] = useState({
      name: "",
      email: "",
      title: "",
      content: "",
      size: "",
    });
    const [rating, setRating] = useState<number>(5);
    const [success, setSuccess] = useState<boolean>(false);
    const [errors, setErrors] = useState<ErrorMap>({});

    const handleChange = ({ target: { name, value } }) => {
      setData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    };

    const handleRatingChange = (quantity: number) => {
      setRating(quantity);
    };

    const handleFitLengthChange = ({
      target: { name },
    }: ChangeEvent<HTMLInputElement>) => {
      return setFitLength((prev) => (prev !== name ? name : prev));
    };
    const handleFitWidthChange = ({
      target: { name },
    }: ChangeEvent<HTMLInputElement>) =>
      setFitWidth((prev) => (prev !== name ? name : prev));

    const handleOptionChange = (value: string) => {
      setData((prevState) => ({
        ...prevState,
        size: value,
      }));
    };

    const handleSubmit = useCallback(
      async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        let result;

        if (!process.env["GATSBY_FEATURE_REVIEWS_ENHANCEMENT"] || !gender) {
          result = schema.safeParse(data);
        } else {
          const mergedSchema = schema.merge(sizeSchema);
          result = mergedSchema.safeParse(data);
        }

        if (!result.success) {
          const mappedErrors = result.error.issues.reduce((map, issue) => {
            return {
              ...map,
              [issue.path[0] ?? "unknown"]: issue.message,
            };
          }, {});

          return setErrors(mappedErrors);
        }
        setErrors({});

        track("Review Write", {
          email: data.email,
          name: data.name,
          review: data.content,
          title: data.title,
          rating,
          sku,
        });
        const { status } = await productReviewAdd(
          data.email,
          data.name,
          data.content,
          data.title,
          rating,
          sku,
        );
        setSuccess(status === "success");
      },
      [data, rating, sku, errors],
    );

    Component.displayName = name;
    return (
      <Component
        data={data}
        handleChange={handleChange}
        rating={rating}
        handleRatingChange={handleRatingChange}
        handleSubmit={handleSubmit}
        className={className}
        loading={loading}
        errors={errors}
        success={success}
        additionalReviewsFormSubmitingText={additionalReviewsFormSubmitingText}
        additionalReviewsFormSubmitText={additionalReviewsFormSubmitText}
        additionalReviewsFormErrorMessage={additionalReviewsFormErrorMessage}
        additionalReviewsFormSuccessTitle={additionalReviewsFormSuccessTitle}
        additionalReviewsFormSuccessMessage={
          additionalReviewsFormSuccessMessage
        }
        handleFitLengthChange={handleFitLengthChange}
        handleFitWidthChange={handleFitWidthChange}
        fitLength={fitLength}
        fitWidth={fitWidth}
        handleOptionChange={handleOptionChange}
        gender={gender}
      />
    );
  };
